import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { haptic } from '../../hooks/useTelegram';
import { useNav } from '../../store/nav';
import { useSession } from '../../hooks/useSession';
import { useOnboarding } from '../../store/onboarding';
import {
  fetchSessions,
  terminateSession,
  terminateOtherSessions,
  type SessionInfo,
} from '../../lib/sessions';
import { mockSessions } from '../../lib/mock';
import { fmtTime, fmtDateHeading } from '../../lib/format';

const DAY_MS = 24 * 60 * 60 * 1000;
const SESSION_TTL_DAYS = 7;

/** Days left before a session expires (7 days after the last open), floored at 0. */
function daysLeft(expiresAt: string): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / DAY_MS));
}

/** Sessions from the mock (dev/browser fallback), mapped to the API shape. */
const mockAsSessions = (): SessionInfo[] =>
  mockSessions.map((s, i) => ({
    id: s.id,
    label: s.device,
    platform: null,
    ip: s.ip,
    lastActive: s.lastActive,
    // Cosmetic in dev: stagger the countdowns so they read sensibly.
    expiresAt: new Date(Date.now() + (SESSION_TTL_DAYS - i) * DAY_MS).toISOString(),
    createdAt: s.createdAt,
    current: !!s.current,
  }));

/** Session management (Profile → Session management): every signed-in device,
 *  per-session terminate and terminate-all-others. Backed by the API in real
 *  Telegram; falls back to the mock list in dev/browser. */
export function SessionsScreen() {
  const { t } = useTranslation();
  const setTab = useNav((s) => s.setTab);
  const resetOnboarding = useOnboarding((s) => s.reset);
  const { token, ready, loading: sessionLoading } = useSession();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['sessions'],
    enabled: !!token,
    queryFn: () => fetchSessions(token!),
  });
  const [busy, setBusy] = useState(false);

  // Show a spinner in a real Telegram client while the session token bootstraps
  // and the list loads; dev/browser (no initData) uses the mock immediately.
  const loading = ready && (sessionLoading || (!!token && q.isLoading));
  const sessions: SessionInfo[] = ready ? (q.data ?? []) : mockAsSessions();
  const others = sessions.filter((s) => !s.current);
  const refresh = () => qc.invalidateQueries({ queryKey: ['sessions'] });

  const terminate = async (id: string) => {
    haptic();
    if (token) {
      setBusy(true);
      await terminateSession(token, id).catch(() => {});
      setBusy(false);
      refresh();
    }
  };

  const terminateAll = async () => {
    haptic('medium');
    if (token) {
      // Real: end every OTHER device; this one stays signed in.
      setBusy(true);
      await terminateOtherSessions(token).catch(() => {});
      setBusy(false);
      refresh();
      return;
    }
    // Dev/browser fallback: behave like a full logout → back to onboarding.
    setTab('wallet');
    resetOnboarding();
  };

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-16 pt-3">
      <header className="flex items-center py-2">
        <h1 className="text-[20px] font-medium text-ink">{t('profile.sessions')}</h1>
      </header>

      <p className="mb-4 mt-1 text-[13px] text-muted">{t('sessions.hint')}</p>

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        </div>
      ) : (
        <>
      <div className="flex flex-col gap-3">
        {sessions.map((s) => (
          <div key={s.id} className="flex flex-col gap-2 rounded-card bg-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-3">
              <p className="min-w-0 flex-1 truncate text-[15px] font-medium text-ink">{s.label}</p>
              {s.current ? (
                <span
                  className="shrink-0 rounded-[6px] px-[6px] py-[3px] text-[11px] font-medium text-positive"
                  style={{ backgroundColor: 'rgb(var(--positive) / 0.16)' }}
                >
                  {t('sessions.current')}
                </span>
              ) : (
                <button
                  onClick={() => terminate(s.id)}
                  disabled={busy}
                  className="shrink-0 text-[13px] font-semibold text-negative active:opacity-70 disabled:opacity-40"
                >
                  {t('sessions.terminate')}
                </button>
              )}
            </div>
            <p className="flex items-center gap-[5px] text-[13px] leading-none text-muted">
              {s.ip && (
                <>
                  IP {s.ip}
                  <span className="inline-block size-[2px] rounded-full bg-muted" />
                </>
              )}
              {t('sessions.lastActive')}: {fmtDateHeading(s.lastActive)}, {fmtTime(s.lastActive)}
            </p>
            <p className="text-[13px] leading-none text-muted">
              {daysLeft(s.expiresAt) > 0
                ? t('sessions.expiresIn', { days: daysLeft(s.expiresAt) })
                : t('sessions.expiresToday')}
            </p>
          </div>
        ))}
      </div>

      {others.length > 0 && (
        <button
          onClick={terminateAll}
          disabled={busy}
          className="mt-6 flex h-[52px] w-full items-center justify-center gap-2 rounded-full bg-card text-[15px] font-bold text-negative shadow-card active:opacity-80 disabled:opacity-60"
        >
          {busy && (
            <span className="size-4 animate-spin rounded-full border-2 border-negative/30 border-t-negative" />
          )}
          {t('sessions.terminateAll')}
        </button>
      )}
        </>
      )}
    </div>
  );
}
